import { THEME } from '@Theme';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { parseNumericDraft } from '../../../../models/utils/numberUtils';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { InputGroupAddon, InputGroupInput, InputGroupV2 as InputGroup } from '../../../../web/components-v2/InputGroup';
import { Pill } from '../../../../web/components-v2/Pill';
import { DownloadIcon } from '../../../../web/components/svgs/icons/DownloadIcon';
import { FileTextIcon } from '../../../../web/components/svgs/icons/FileTextIcon';
import { LinkIcon } from '../../../../web/components/svgs/icons/LinkIcon';
import { SquareLinkIcon } from '../../../../web/components/svgs/icons/SquareLinkIcon';
import { useAdminAccountStatementPdfMutation } from '../../../../queries/Admin/Account/useAdminAccountStatementPdfMutation';
import { useAdminProformaInvoiceUrlMutation } from '../../../../queries/Admin/Account/useAdminProformaInvoiceUrlMutation';
import { useAdminSelfServicePaymentUrlMutation } from '../../../../queries/Admin/Account/useAdminSelfServicePaymentUrlMutation';
import {
	billingCycleDescription,
	billingTypeDescription,
	extraUsersDisplay,
	isPaidAccount,
	isPastDueAccount,
	isPendingCancellation,
	isStillInContract,
	isTrialExpired,
	lastBillingAmountDisplay,
	lastBillingDateDisplay,
	mrrDisplay,
	renewalDateLabel,
	renewalOrExpireDateDescription,
} from '../accountOverviewFormatters';
import { useSubscriptionStateQuery } from '../../../../queries/Admin/UpgradeWizard/useSubscriptionStateQuery';
import { billingCardStyles } from './billingCardStyles';
import { OverviewCard } from './OverviewCard';
import { OverviewField } from './OverviewField';
import { ExtendTrialDialog, StopCancellationDialog } from './BillingRenewalDialogs';
import { hasPlanDetails, PlanDetailsPanel } from './PlanDetailsPanel';
import { RenewalDateSubLines } from './RenewalDateSubLines';
import { hasRenewalInfo, RenewalInfoPanel } from './RenewalInfoPanel';
import { overviewFieldStyles } from './overviewFieldStyles';
import type { ICardEditMode } from './useCardEditMode';

/** Accent color for the Billing card's ModuleTag header (enchantment-blue hover accent, #00ccff). */
const BILLING_ACCENT = THEME.colors.hover.main;

export interface IBillingCardProps {
	/** The account whose billing is being shown / edited. */
	account: Api.IAccount;
	/** Whether this card is editable at all (admin holds `ModifyAccount`). */
	canEdit: boolean;
	/** Whether a save is currently in flight. */
	isSaving: boolean;
	/** Commit the current card draft (parent owns the mutation). */
	onSave: () => void;
	/** Parent-owned per-card edit state from `useCardEditMode`. */
	edit: ICardEditMode<Api.IAccount>;
	/** Company name of the linked master account (Comp accounts with a `mainAccountId`); the parent fetches it. */
	mainAccountName?: string;
}

/**
 * Warns that a billing-cycle change is already scheduled to take effect at the next renewal, so the
 * cycle shown is not what the account will be on. Only queries when the account actually has billing.
 */
const BillingCyclePendingIndicator = ({ accountId, hasBilling }: { accountId: string; hasBilling: boolean }) => {
	const { data } = useSubscriptionStateQuery({ accountId, enabled: !!accountId && hasBilling });
	if (!data?.nextProductHandle) {
		return null;
	}
	return (
		<div className={css(billingCardStyles.pendingBillingCycleChangeText)}>Pending change scheduled (next renewal)</div>
	);
};

/**
 * The subscription state this card reports. A cancellation *on file* against a still-active
 * subscription is "pending cancel", NOT cancelled — collapsing the two read as a contradiction next
 * to an Active account status, which is how Success reads the page.
 */
type SubscriptionState = 'active' | 'pendingCancel' | 'cancelled';

const subscriptionState = (account: Api.IAccount): SubscriptionState => {
	if (account.activationStatus === Api.ActivationStatus.CANCELED) {
		return 'cancelled';
	}
	return isPendingCancellation(account) ? 'pendingCancel' : 'active';
};

/** Header pill + banner copy for each subscription state. */
const SUBSCRIPTION_STATE_DISPLAY: Record<
	SubscriptionState,
	{ label: string; color: 'green' | 'red' | 'orange'; banner: string }
> = {
	active: { label: 'Active', color: 'green', banner: 'Subscription is active — no cancellation on file.' },
	pendingCancel: {
		label: 'Pending Cancel',
		color: 'red',
		banner: 'Subscription is active with a cancellation on file (pending cancel).',
	},
	cancelled: { label: 'Cancelled', color: 'red', banner: 'Subscription is cancelled.' },
};

/** Plan label: planName plus the parenthesized billing type, e.g. "Business (Comp)". */
const planLabel = (account?: Api.IAccount): string => {
	const planName = account?.planDetails?.planName ?? 'Unknown';
	return `${planName} (${billingTypeDescription(account)})`;
};

/**
 * Chargify id as a display string, bound to the draft while editing. Legacy seeds this field from an
 * empty string (never the account value), so an unset id — `0`, null, or undefined — renders empty and
 * the admin types the new id in; it is parsed back onto the draft on change.
 */
const chargifyIdValue = (account?: Api.IAccount): string => {
	const id = account?.planDetails?.billingReferenceId;
	return id ? String(id) : '';
};

/** Renewal discount display: a 0..100 percentage as "n%", otherwise "0%". */
const renewalDiscountDisplay = (account?: Api.IAccount): string => {
	const value = account?.additionalInfo?.renewalDiscountPercentage;
	return typeof value === 'number' && value >= 0 && value <= 100 ? `${value}%` : '0%';
};

/** Maxio (Chargify) customer page for a billing reference id; dev/test points at the clone site. */
const maxioCustomerUrl = (chargifyCustomerId: number): string =>
	encodeURI(
		`https://real-magic${Api.isDevOrTestEnvironment ? '-clone' : ''}.chargify.com/customers/${chargifyCustomerId}`
	);

/**
 * Account Overview "Billing" card. Renders a read view (status pill, active /
 * cancelled banner, Plan / Amounts / Components / Actions subsections) and, when the
 * parent flips the card into edit mode with the `ModifyAccount` permission, an edit
 * view whose Plan and Amounts fields become components-v2 controls bound to the draft.
 *
 * Edit state is parent-owned via `useCardEditMode`: the card calls `edit.beginEdit`,
 * `edit.cancel`, and `edit.setDraftField`, and defers the commit to `onSave`.
 */
export const BillingCard = ({ account, canEdit, isSaving, onSave, edit, mainAccountName }: IBillingCardProps) => {
	const { isEditMode, isModifyingAccount, draft, setDraftField, beginEdit, cancel } = edit;

	// In edit mode the controls bind to the working draft; everything else reads the account.
	const source = isModifyingAccount && draft ? draft : account;
	const state = subscriptionState(account);
	const stateDisplay = SUBSCRIPTION_STATE_DISPLAY[state];
	const isTrialAccount = account.planDetails?.billingType === Api.BillingType.Trial;
	// Both renewal actions persist through their own endpoints, so they are independent of the card draft.
	const [isStopCancellationOpen, setIsStopCancellationOpen] = useState(false);
	const [isExtendTrialOpen, setIsExtendTrialOpen] = useState(false);

	const components = account.planDetails?.additionalComponents ?? [];

	// Legacy parity: a Comp account with a plan but no Chargify subscription can be linked either to
	// Chargify (billingReferenceId) or to a master account (mainAccountId); a Trial account gets only
	// the Chargify id. The gates read the original `account`, not the draft, so typing an id doesn't
	// flip the gate and make the control disappear mid-edit (legacy held the value in separate state).
	const [compLinkMode, setCompLinkMode] = useState<'chargify' | 'mainAccount'>('chargify');
	const billingType = account.planDetails?.billingType;
	const planId = account.planDetails?.planId ?? 0;
	const hasChargifyId = (account.planDetails?.billingReferenceId ?? 0) > 0;
	const canEditPlanLink = isModifyingAccount && planId >= 1 && !hasChargifyId;
	const showCompLinkEditor = canEditPlanLink && billingType === Api.BillingType.Comp;
	const showChargifyIdInput = canEditPlanLink && billingType !== Api.BillingType.Paid;
	// A Comp account already linked to a master shows a read-only "Main: <account>" link (legacy parity).
	const showMainAccountLink = billingType === Api.BillingType.Comp && !!account.mainAccountId;
	const isPastDue = isPastDueAccount(account);

	const chargifyCustomerId = account.planDetails?.billingReferenceId ?? 0;
	const statementPdfMutation = useAdminAccountStatementPdfMutation();
	const selfServicePaymentUrlMutation = useAdminSelfServicePaymentUrlMutation();
	const proformaInvoiceUrlMutation = useAdminProformaInvoiceUrlMutation();

	const openBillingUtilityUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

	return (
		<OverviewCard
			title='Billing'
			color={BILLING_ACCENT}
			canEdit={canEdit}
			isEditMode={isEditMode}
			isSaving={isSaving}
			onEdit={() => beginEdit(account)}
			onSave={onSave}
			onCancel={cancel}
			headerAccessory={<Pill variant='label' color={stateDisplay.color} label={stateDisplay.label} />}
		>
			<div className={css(billingCardStyles.banner)}>
				<span
					className={css(
						billingCardStyles.bannerDot,
						state === 'cancelled' && billingCardStyles.bannerDotCancelled,
						state === 'pendingCancel' && billingCardStyles.bannerDotPendingCancel
					)}
				/>
				<span className={css(billingCardStyles.bannerText)}>{stateDisplay.banner}</span>
			</div>

			<div className={css(overviewFieldStyles.grid)}>
				{/*
				 * Legacy parity: Plan is read-only except that an un-linked Comp/Trial account can be linked
				 * to Chargify (or, for Comp, a master account) here. Billing Cycle, Renewal Date, MRR, Extra
				 * Users, Last Billing Amount / Date and Components are driven by Chargify / plan configuration.
				 */}
				<OverviewField label='Plan' info={hasPlanDetails(account) ? <PlanDetailsPanel account={account} /> : undefined}>
					{showCompLinkEditor ? (
						<div className={css(billingCardStyles.planEdit)}>
							{compLinkMode === 'chargify' ? (
								<>
									<Input
										value={chargifyIdValue(source)}
										placeholder='Chargify ID'
										spellCheck={false}
										onChange={e => setDraftField('planDetails.billingReferenceId', parseNumericDraft(e.target.value))}
									/>
									<Button
										variant='link'
										styles={[billingCardStyles.planLinkToggle]}
										onClick={() => setCompLinkMode('mainAccount')}
									>
										Link to master account instead
									</Button>
								</>
							) : (
								<>
									<Input
										value={source.mainAccountId ?? ''}
										placeholder='Main Account ID'
										onChange={e => setDraftField('mainAccountId', e.target.value)}
									/>
									<Button
										variant='link'
										styles={[billingCardStyles.planLinkToggle]}
										onClick={() => setCompLinkMode('chargify')}
									>
										Link to Chargify subscription instead
									</Button>
								</>
							)}
						</div>
					) : showChargifyIdInput ? (
						<div className={css(billingCardStyles.planEdit)}>
							<span>{planLabel(source)}</span>
							<Input
								value={chargifyIdValue(source)}
								placeholder='Chargify ID'
								spellCheck={false}
								onChange={e => setDraftField('planDetails.billingReferenceId', parseNumericDraft(e.target.value))}
							/>
						</div>
					) : (
						<>
							{planLabel(source)}
							{isPastDue && <span className={css(billingCardStyles.pastDueText)}> PAST DUE</span>}
							{showMainAccountLink && (
								<span className={css(billingCardStyles.mainAccountLink)}>
									Main:{' '}
									<a
										className={css(billingCardStyles.mainAccountAnchor)}
										href={`${window.location.origin}/admin.html#/accounts/${account.mainAccountId}`}
										target='_blank'
										rel='noreferrer'
									>
										{mainAccountName || account.mainAccountId}
									</a>
								</span>
							)}
						</>
					)}
				</OverviewField>
				<OverviewField label='Billing Cycle' muted>
					{billingCycleDescription(source)}
					<BillingCyclePendingIndicator accountId={account.id} hasBilling={!!account.planDetails?.billingReferenceId} />
				</OverviewField>
				<OverviewField
					label={renewalDateLabel(source)}
					muted
					tone={state === 'pendingCancel' ? 'danger' : 'default'}
					info={
						// Gate on the predicate, not the element: a component that returns null is still a
						// truthy prop, which would leave an info icon opening an empty card.
						hasRenewalInfo(account) ? (
							<RenewalInfoPanel
								account={account}
								onStopCancellation={canEdit ? () => setIsStopCancellationOpen(true) : undefined}
							/>
						) : undefined
					}
				>
					{renewalOrExpireDateDescription(source)}
					{/* Legacy offers the extension while the trial has not been extended yet, or once an
						    extended trial has run out. */}
					{canEdit && isTrialAccount && (isTrialExpired(account) || !account.planDetails?.trialExtension) && (
						<Button
							variant='link'
							styles={[billingCardStyles.trialActionButton]}
							onClick={() => setIsExtendTrialOpen(true)}
						>
							Extend Trial
						</Button>
					)}
					{/* A trial that has already been extended says so; legacy only shows this while it is still
						    within the extended window. */}
					{!!account.planDetails?.trialExtension && !isTrialExpired(account) && (
						<div className={css(billingCardStyles.trialExtendedText)}>Trial Extended</div>
					)}
					<RenewalDateSubLines
						pendingCancellation={state === 'pendingCancel'}
						stillInContract={isStillInContract(source)}
					/>
				</OverviewField>
				<OverviewField label='Renewal Discount'>
					{isModifyingAccount ? (
						<InputGroup>
							<InputGroupInput
								value={
									source.additionalInfo?.renewalDiscountPercentage != null
										? String(source.additionalInfo.renewalDiscountPercentage)
										: ''
								}
								placeholder='0'
								onChange={e =>
									setDraftField('additionalInfo.renewalDiscountPercentage', parseNumericDraft(e.target.value))
								}
							/>
							<InputGroupAddon align='inlineEnd'>%</InputGroupAddon>
						</InputGroup>
					) : (
						renewalDiscountDisplay(source)
					)}
				</OverviewField>
				<OverviewField label='MRR' muted>
					{mrrDisplay(source)}
				</OverviewField>
				<OverviewField label='Extra Users' muted>
					{extraUsersDisplay(source)}
				</OverviewField>
				<OverviewField label='Last Billing Amount' muted>
					{lastBillingAmountDisplay(source)}
				</OverviewField>
				<OverviewField label='Last Billing Date'>{lastBillingDateDisplay(source)}</OverviewField>
				{/* Additional components is a derived string[], not free text — read-only. */}
				<OverviewField label='Plan Additional Components' spanTwo muted={components.length === 0}>
					{components.length > 0 ? (
						<span className={css(billingCardStyles.componentPills)}>
							{components.map(component => (
								<Pill key={component} variant='label' color='green' label={component} />
							))}
						</span>
					) : (
						'Empty'
					)}
				</OverviewField>
			</div>

			{!isModifyingAccount && isPaidAccount(account) && !!account.planDetails?.billingReferenceId && (
				<div className={css(billingCardStyles.actionRow)}>
					<Button
						variant='secondary'
						styles={[billingCardStyles.actionButton]}
						disabled={statementPdfMutation.isPending}
						onClick={() => statementPdfMutation.mutate({ accountId: account.id, companyName: account.companyName })}
					>
						<DownloadIcon />
						{statementPdfMutation.isPending ? 'Downloading…' : 'Download Last Statement'}
					</Button>
					<Button
						variant='secondary'
						styles={[billingCardStyles.actionButton]}
						onClick={() => openBillingUtilityUrl(maxioCustomerUrl(chargifyCustomerId))}
					>
						<SquareLinkIcon />
						Maxio
					</Button>
					<Button
						variant='secondary'
						styles={[billingCardStyles.actionButton]}
						disabled={selfServicePaymentUrlMutation.isPending}
						onClick={() =>
							selfServicePaymentUrlMutation.mutate({ chargifyCustomerId }, { onSuccess: openBillingUtilityUrl })
						}
					>
						<LinkIcon />
						{selfServicePaymentUrlMutation.isPending ? 'Getting link…' : 'Self-Service Payment Link'}
					</Button>
					<Button
						variant='secondary'
						styles={[billingCardStyles.actionButton]}
						disabled={proformaInvoiceUrlMutation.isPending}
						onClick={() =>
							proformaInvoiceUrlMutation.mutate({ chargifyCustomerId }, { onSuccess: openBillingUtilityUrl })
						}
					>
						<FileTextIcon width={16} height={16} />
						{proformaInvoiceUrlMutation.isPending ? 'Getting link…' : 'Preview Next Invoice'}
					</Button>
				</div>
			)}

			{isStopCancellationOpen && (
				<StopCancellationDialog account={account} open onClose={() => setIsStopCancellationOpen(false)} />
			)}
			{isExtendTrialOpen && <ExtendTrialDialog account={account} open onClose={() => setIsExtendTrialOpen(false)} />}
		</OverviewCard>
	);
};
